import {
	I18nFormProvider,
	PagedSingleSelectField,
	isSuccess,
	laravelServiceSelectFetchConfig,
	useDynamicConfig,
	useHasPermission,
	useToasts,
	withOptionsEndpoint,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { organizerService } from "../api/organizerService";
import { OrganizerFormValues, OrganizerOptionResource, OrganizerType } from "../types/organizer";
import { PermissionName } from "../types/permission";
import { OrganizerFormDrawerContent, useValidateOrganizer } from "./OrganizerFormDrawerContent";

const StyledForm = styled(Form)`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 100%;
`;

interface Props {
	name: string;
	label: string;
	clearable?: boolean;
	required?: boolean;
	disabled?: boolean;
}

const service = withOptionsEndpoint<OrganizerOptionResource>(["organizers"]);
const fetchConfig = laravelServiceSelectFetchConfig(service);

export const OrganizerSingleSelect: FC<Props> = ({ name, label, clearable, required, disabled }) => {
	const { t } = useTranslation();
	const { languageSettings } = useDynamicConfig();
	const { showToast } = useToasts();
	const validate = useValidateOrganizer();
	const queryClient = useQueryClient();
	const canCreateOrganizer = useHasPermission<PermissionName>(["organizers.create"]);

	const initialValues: OrganizerFormValues = useMemo(
		() => ({
			name: "",
			type: OrganizerType.COMPANY,
			translations: [
				{ locale: languageSettings.fallback, description: [{ type: "paragraph", children: [{ text: "" }] }] },
			],
		}),
		[languageSettings.fallback],
	);

	const createNewOrganizer = useCallback(
		async (
			values: OrganizerFormValues,
			selectNewOption: (option: OrganizerOptionResource) => void,
			onClose: () => void,
		) => {
			const res = await organizerService.store(values);

			if (isSuccess(res)) {
				showToast({ type: "success", title: t("Organizer"), body: t("Organizer was successfully created.") });
				void queryClient.invalidateQueries({ queryKey: organizerService.indexKey });
				selectNewOption({ id: res.data.data.id, name: values.name, description: null });
				onClose();
			}
		},
		[queryClient, showToast, t],
	);

	return (
		<PagedSingleSelectField
			name={name}
			label={label}
			clearable={clearable}
			required={required}
			disabled={disabled}
			fetchConfig={fetchConfig}
			renderCreateForm={
				!canCreateOrganizer
					? undefined
					: (selectNewOption, onClose, search) => (
							<I18nFormProvider initialLanguage={languageSettings.fallback}>
								<Formik
									initialValues={{ ...initialValues, name: search || initialValues.name }}
									validate={validate}
									onSubmit={(values) => createNewOrganizer(values, selectNewOption, onClose)}
								>
									<StyledForm>
										<OrganizerFormDrawerContent onClose={onClose} title={t("Create organizer")} />
									</StyledForm>
								</Formik>
							</I18nFormProvider>
						)
			}
			createButtonTitle={t("Create organizer")}
		/>
	);
};
